import CardLayout from "components/Common/CardLayout"
import { formatReadableDate } from "utils/formatDate"
import MovementActivity from "components/Common/MovementActivity/MovementActivity"
import { EmployeeLink } from "components/Common/EmployeeLink"
import ActivitySummaryCard from "components/Common/ActivitySummaryCard/ActivitySummaryCard"
import { useState } from "react"
import ToggleButton from "components/Common/ToggleButton"

const VehicleMovements = ({ data = [] }) => {
  const icon = "bx bx-map"
  const title = "Vehicle Movement & Fuel Refill Activity"
  const action = ""

  const buildMovementActivityItem = (movement, nextMovement) => {
    const type = movement?.movement_type?.name ?? "Unknown Type"
    const from = movement?.location_from?.name ?? "Unknown Location"
    const to = movement?.location_to?.name ?? "Unknown Location"
    const mileage = movement?.mileage ?? 0
    const nextMileage = nextMovement?.mileage ?? mileage

    // Since movements are DESC by movement_date (most recent first)
    // distance should be based on mileage difference from NEXT (older) record
    const distance =
      mileage != null && nextMileage != null ? mileage - nextMileage : null

    const date = formatReadableDate(movement?.movement_date)

    // EmployeeLink as JSX element
    const employeeName = (
      <EmployeeLink
        name={movement?.movement_by?.name}
        username={movement?.movement_by?.user?.username}
      />
    )

    return {
      title: type,
      details: (
        <>
          Moved from {from} → {to} by {employeeName} (Mileage:
          {mileage.toLocaleString()} km)
        </>
      ),
      distance: `${distance.toLocaleString()} km travelled`,
      date,
      type,
    }
  }

  const buildFuelUsageActivityItem = (fuelUsage, prevFuelUsage) => {
    const type = "Fuel Refilling"
    const mileage = fuelUsage?.mileage ?? 0
    const fuel_qty = fuelUsage?.fuel_qty ?? 0
    const prevMileage = prevFuelUsage?.mileage ?? mileage
    const prevFuel = prevFuelUsage?.fuel_qty ?? fuel_qty

    // distance based on mileage difference from previous (older) record
    const distance =
      mileage != null && prevMileage != null ? mileage - prevMileage : null

    const date = formatReadableDate(fuelUsage?.uses_date)

    const fuelUsed = parseFloat(prevFuel)
    const efficiency = fuelUsed > 0 ? (distance / fuelUsed).toFixed(2) : 0

    // EmployeeLink as JSX element
    const employeeName = (
      <EmployeeLink
        name={fuelUsage?.fuel_user?.name}
        username={fuelUsage?.fuel_user?.user?.username}
      />
    )

    return {
      title: type,
      // Use JSX fragment to include EmployeeLink
      details: (
        <>
          {employeeName} used {parseFloat(fuelUsage.fuel_qty).toFixed(2)}L fuel{" "}
          (Mileage: {fuelUsage.mileage.toLocaleString()} km). Remarks:{" "}
          {fuelUsage.remarks ?? "None"}
        </>
      ),
      distance: `${distance.toLocaleString()} km travelled by ${prevFuel.toLocaleString()} L (${efficiency} km/L).`,
      date,
      type,
    }
  }

  const movementFuelMerged = (fuel_usages = [], movements = []) => {
    // Tag each array (DO NOT change order)
    const formattedMovements = movements.map(m => ({
      ...m,
      type: "movement",
      date: new Date(m.date),
    }))

    const formattedFuels = fuel_usages.map(f => ({
      ...f,
      type: "fuel",
      date: new Date(f.date),
    }))

    // Merge both arrays but maintain DESC order
    const merged = [...formattedMovements, ...formattedFuels].sort(
      (a, b) => b.date - a.date
    )
    return merged
  }

  const movements = data?.movements ?? []
  const fuelUsages = data?.fuel_usages ?? []

  const movementData = movements.map((item, index) =>
    buildMovementActivityItem(item, movements[index + 1])
  )
  const fuelUsageData = fuelUsages.map((item, index) =>
    buildFuelUsageActivityItem(item, fuelUsages[index + 1])
  )

  const mergedData = movementFuelMerged(fuelUsageData, movementData)

  function calculateAccurateFuelPrediction(movements = [], fuelUsages = []) {
    if (!Array.isArray(movements) || !Array.isArray(fuelUsages)) return {}

    // Sort data by mileage (descending - most recent first)
    const sortedMovements = [...movements].sort((a, b) => b.mileage - a.mileage)
    const sortedFuelUsages = [...fuelUsages].sort(
      (a, b) => b.mileage - a.mileage
    )

    const currentMileage = sortedMovements[0]?.mileage || 0
    const latestRefuel = sortedFuelUsages[0] || {}
    const latestRefuelMileage = latestRefuel.mileage || currentMileage
    const latestRefuelQty = parseFloat(latestRefuel.fuel_qty || 0)

    // --- TOYOTA VEHICLE PROFILES ---
    const toyotaProfiles = {
      hybrid: {
        urban: 18.5, // km/L city driving
        highway: 22.5, // km/L highway driving
        mixed: 20.5, // km/L mixed driving
        tankCapacity: 43, // liters (typical Toyota hybrid)
      },
      nonHybrid: {
        urban: 12.5, // km/L city driving
        highway: 16.5, // km/L highway driving
        mixed: 14.5, // km/L mixed driving
        tankCapacity: 55, // liters (typical Toyota sedan)
      },
      suv: {
        urban: 10.5, // km/L city driving
        highway: 13.5, // km/L highway driving
        mixed: 12.0, // km/L mixed driving
        tankCapacity: 65, // liters (typical Toyota SUV)
      },
    }

    // --- ANALYZE DRIVING PATTERNS ---
    const analyzeDrivingPattern = movements => {
      if (movements.length < 2) return "mixed"

      // Analyze movement types to determine driving pattern
      const movementTypes = movements.map(m => m.movement_type?.name || "")
      const urbanMovements = [
        "Parking",
        "Customer Delivery",
        "BRTA Registration",
      ]
      const highwayMovements = ["Transfer", "Test Drive"]

      let urbanCount = 0
      let highwayCount = 0

      movementTypes.forEach(type => {
        if (urbanMovements.some(urban => type.includes(urban))) urbanCount++
        if (highwayMovements.some(highway => type.includes(highway)))
          highwayCount++
      })

      if (urbanCount > highwayCount * 2) return "urban"
      if (highwayCount > urbanCount * 2) return "highway"
      return "mixed"
    }

    // --- DETERMINE VEHICLE TYPE BASED ON USAGE PATTERNS ---
    const determineVehicleType = (fuelUsages, movements) => {
      if (fuelUsages.length === 0) return "nonHybrid" // Default assumption

      // Analyze refuel quantities to guess vehicle type
      const refuelQuantities = fuelUsages.map(f => parseFloat(f.fuel_qty || 0))
      const avgRefuelQty =
        refuelQuantities.reduce((a, b) => a + b, 0) / refuelQuantities.length

      if (avgRefuelQty <= 15) return "hybrid" // Small, frequent refuels suggest hybrid
      if (avgRefuelQty >= 40) return "suv" // Large refuels suggest SUV
      return "nonHybrid" // Medium refuels suggest sedan
    }

    const drivingPattern = analyzeDrivingPattern(sortedMovements)
    const vehicleType = determineVehicleType(sortedFuelUsages, sortedMovements)
    const vehicleProfile = toyotaProfiles[vehicleType]

    // --- CALCULATE ACTUAL CONSUMPTION PATTERNS ---
    let actualEfficiencies = []
    let totalHistoricalFuel = 0
    let totalHistoricalDistance = 0

    for (let i = 0; i < sortedFuelUsages.length - 1; i++) {
      const currentRefuel = sortedFuelUsages[i]
      const nextRefuel = sortedFuelUsages[i + 1]

      if (
        currentRefuel.mileage &&
        nextRefuel.mileage &&
        currentRefuel.mileage > nextRefuel.mileage
      ) {
        const distance = currentRefuel.mileage - nextRefuel.mileage
        const fuelUsed = parseFloat(currentRefuel.fuel_qty || 0)

        if (distance > 0 && fuelUsed > 0) {
          const efficiency = distance / fuelUsed
          actualEfficiencies.push(efficiency)
          totalHistoricalFuel += fuelUsed
          totalHistoricalDistance += distance
        }
      }
    }

    // --- ACCURATE FUEL ESTIMATION LOGIC ---
    let estimatedFuelLeft = 0
    let estimatedMaxRange = 0
    let efficiencyUsed = 0
    let estimationMethod = ""

    const distanceSinceLastRefuel = currentMileage - latestRefuelMileage

    if (actualEfficiencies.length > 0) {
      // Use actual historical data with weighted average (recent patterns matter more)
      const weightedEfficiency =
        actualEfficiencies.reduce((sum, eff, index) => {
          const weight = (index + 1) / actualEfficiencies.length // Recent patterns get higher weight
          return sum + eff * weight
        }, 0) /
        actualEfficiencies.reduce(
          (sum, _, index) => sum + (index + 1) / actualEfficiencies.length,
          0
        )

      efficiencyUsed = weightedEfficiency
      estimationMethod = "historical_pattern"
    } else {
      // Use Toyota profile data based on driving pattern
      efficiencyUsed = vehicleProfile[drivingPattern]
      estimationMethod = "toyota_profile"
    }

    // Realistic fuel consumption calculation
    const fuelConsumedSinceRefuel = distanceSinceLastRefuel / efficiencyUsed
    estimatedFuelLeft = Math.max(latestRefuelQty - fuelConsumedSinceRefuel, 0)

    // Realistic max range based on actual refuel behavior
    if (sortedFuelUsages.length > 0) {
      const refuelQuantities = sortedFuelUsages.map(f =>
        parseFloat(f.fuel_qty || 0)
      )
      const avgRefuelQty =
        refuelQuantities.reduce((a, b) => a + b, 0) / refuelQuantities.length
      estimatedMaxRange = avgRefuelQty * efficiencyUsed
    } else {
      estimatedMaxRange = vehicleProfile.tankCapacity * efficiencyUsed
    }

    // --- SMART REFILL PREDICTION ---
    const remainingMileageBeforeRefill = Math.max(
      estimatedMaxRange - distanceSinceLastRefuel,
      0
    )

    // Dynamic threshold based on vehicle type and driving pattern
    const baseThreshold = 0.15 // 15% remaining range
    const adjustedThreshold = estimatedMaxRange * baseThreshold
    const needsRefill = remainingMileageBeforeRefill <= adjustedThreshold

    // --- ENHANCED NEXT REFUEL PREDICTION ---
    let predictedNextRefuelMileage = null
    let predictedNextRefuelDate = null

    if (currentMileage > 0) {
      predictedNextRefuelMileage = Math.round(
        currentMileage + remainingMileageBeforeRefill
      )

      // Calculate average daily distance from movement patterns
      if (sortedMovements.length >= 2) {
        const recentMovements = sortedMovements.slice(
          0,
          Math.min(5, sortedMovements.length)
        )
        const totalDays = Math.max(
          1,
          (new Date(recentMovements[0].movement_date) -
            new Date(
              recentMovements[recentMovements.length - 1].movement_date
            )) /
            (1000 * 60 * 60 * 24)
        )
        const totalDistance =
          recentMovements[0].mileage -
          recentMovements[recentMovements.length - 1].mileage
        const avgDailyDistance = totalDistance / totalDays

        if (avgDailyDistance > 0) {
          const daysUntilRefuel = Math.ceil(
            remainingMileageBeforeRefill / avgDailyDistance
          )
          const nextDate = new Date(sortedMovements[0].movement_date)
          nextDate.setDate(nextDate.getDate() + daysUntilRefuel)
          predictedNextRefuelDate = nextDate.toISOString().split("T")[0]
        }
      }
    }

    // --- CONFIDENCE CALCULATION ---
    let confidence = "low"
    if (actualEfficiencies.length >= 3) confidence = "high"
    else if (actualEfficiencies.length >= 1) confidence = "medium"

    return {
      totalRunByMovement: (
        currentMileage -
        (sortedMovements[sortedMovements.length - 1]?.mileage || currentMileage)
      ).toFixed(2),
      totalFuelByFuelUsage: sortedFuelUsages
        .reduce((sum, fuel) => sum + parseFloat(fuel.fuel_qty || 0), 0)
        .toFixed(2),
      efficiency: efficiencyUsed.toFixed(2),
      avgRunPerRefuel:
        actualEfficiencies.length > 0
          ? (totalHistoricalDistance / actualEfficiencies.length).toFixed(2)
          : estimatedMaxRange.toFixed(2),
      avgEfficiencyPerRefuel:
        actualEfficiencies.length > 0
          ? (totalHistoricalDistance / totalHistoricalFuel).toFixed(2)
          : efficiencyUsed.toFixed(2),
      estimatedMaxRange: estimatedMaxRange.toFixed(2),
      distanceSinceLastRefuel: distanceSinceLastRefuel.toFixed(2),
      remainingMileageBeforeRefill: remainingMileageBeforeRefill.toFixed(2),
      estimatedFuelLeft: estimatedFuelLeft.toFixed(2),
      needsRefill,
      predictionMethod: estimationMethod,
      fuelEstimationMethod: estimationMethod,
      consumptionPatterns: actualEfficiencies.length,
      predictedNextRefuelMileage,
      predictedNextRefuelDate,
      refillThreshold: adjustedThreshold.toFixed(2),
      confidence,
      vehicleType,
      drivingPattern,
      vehicleProfile: `${vehicleType} (${drivingPattern})`,
    }
  }

  // Usage with your data
  const stats = calculateAccurateFuelPrediction(movements, fuelUsages)

  // Summary state handle
  const [showSummary, setShowSummary] = useState(false)

  return (
    <CardLayout icon={icon} title={title} action={action}>
      <ToggleButton openLabel="Hide Summary" closedLabel="Show Summary">
        <div className="mb-3">
          <ActivitySummaryCard
            movement={movements.length}
            fuelRefill={fuelUsages.length}
            totalDistance={parseFloat(stats.totalRunByMovement)}
            totalFuel={parseFloat(stats.totalFuelByFuelUsage)}
            efficiency={parseFloat(stats.efficiency)}
            avgRunPerRefuel={parseFloat(stats.avgRunPerRefuel)}
            avgEfficiencyPerRefuel={parseFloat(stats.avgEfficiencyPerRefuel)}
            estimatedMaxRange={parseFloat(stats.estimatedMaxRange)}
            distanceSinceLastRefuel={parseFloat(stats.distanceSinceLastRefuel)}
            remainingMileageBeforeRefill={parseFloat(
              stats.remainingMileageBeforeRefill
            )}
            estimatedFuelLeft={parseFloat(stats.estimatedFuelLeft)}
            needsRefill={stats.needsRefill}
            predictionMethod={stats.predictionMethod}
            fuelEstimationMethod={stats.fuelEstimationMethod}
            consumptionPatterns={stats.consumptionPatterns}
            predictedNextRefuelMileage={stats.predictedNextRefuelMileage}
            predictedNextRefuelDate={stats.predictedNextRefuelDate}
            refillThreshold={parseFloat(stats.refillThreshold)}
            confidence={stats.confidence}
            // ADD THESE MISSING PARAMETERS:
            vehicleType={stats.vehicleType}
            drivingPattern={stats.drivingPattern}
            vehicleProfile={stats.vehicleProfile}
          />
        </div>
      </ToggleButton>

      {mergedData.length > 0 ? (
        <MovementActivity data={mergedData} />
      ) : (
        <p className="text-muted text-center mb-0 py-4">
          No movement records available.
        </p>
      )}
    </CardLayout>
  )
}

export default VehicleMovements
